import {
  getTendersURLs,
  getTenderVersionedFileLocation,
  newSingleTenderDownload,
} from '~src/services/api';
import { saveFilesToZip } from '~src/utils/downloader/shared';
import { DownloadMultipleAttachmentsParams } from '~src/utils/downloader/types/types';
import { DownloadingState, useStore } from '~src/store';
import * as zip from '@zip.js/zip.js';
import streamSaver from 'streamsaver';

class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  private fileMap: Map<string, number> = new Map();
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  public totalSize = 0;
  private startTime = 0;
  public endTime = 0;
  public isCancelled = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isCancelled = true;
    }
  }

  cancel() {
    this.isCancelled = true;
    this.writableStream = null;
    this.zipWriter = null;
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;

    // Extract base name and extension
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath; // File name without (x)
    const extension = match?.[3] || ''; // File extension (e.g., .jpg)

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    // Store the new name to prevent future conflicts
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;

    const uniqueName = this.getUniqueFileName(name);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));

      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);

      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: Multiple`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: Multiple`
        );
      this.isCancelled = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isCancelled || !this.zipWriter) return;

    try {
      // Ensure folder path has a trailing slash
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));

      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      this.errorLog.push(
        `${folderPath} - ERROR: Failed to create empty folder`
      );
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;

      if (this.endTime !== 0) {
        return;
      }

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      //  setTimeout(() => clearPanel(), 3000);
    } catch (error) {
      this.isCancelled = true;
    }
  }
}

// Use Workers
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }

              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              console.error(\`Worker Error - File: \${file.file_name}, Error: \${error.message}\`);
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

export const downloadMultipleVersionFiles = async (
  files,
  tenderId,
  projectId
) => {
  // console.log(`downloadMultipleVersionFiles ->`, files);
  if (!files.length) throw new Error('No files selected');

  const NUM_WORKERS = 5;
  const totalFiles = files.length;
  let totalSize = 0;
  const fileQueue: any[] = [];

  if (!tenderId || !projectId) throw new Error('Invalid tender or project ID');

  useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);

  const fileIdsArray = files.map((file) => {
    if (!file.id) throw new Error(`File missing ID`);
    return file.id;
  });

  try {
    const registrationKeys = await newSingleTenderDownload('registration key', {
      projectId,
      tenderId,
      file_ids: fileIdsArray,
    });

    if (!registrationKeys.length) throw new Error('No registration keys found');

    for (const file of registrationKeys) {
      fileQueue.push({
        file,
        fileName: file.file_name,
      });
      totalSize += file.file_size || 0;
    }
  } catch (error) {
    console.error(`Error processing files:`, error);
  }

  if (fileQueue.length === 0) {
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
    return;
  }

  const zipStream = new ZipStream(totalFiles, totalSize);
  zipStream.start();
  if (zipStream.isCancelled) return;

  const workers: Worker[] = [];
  let activeWorkers = 0;

  for (let i = 0; i < NUM_WORKERS; i++) {
    workers.push(createWorker());
  }

  const assignFileToWorker = async (worker: Worker) => {
    if (zipStream.isCancelled) return;

    const nextFileObj = fileQueue.shift();
    if (!nextFileObj) return;

    activeWorkers++;

    try {
      const fileUrl = await getTendersURLs(projectId, tenderId, [
        nextFileObj.file.using_key,
      ]);

      if (!fileUrl[0] || !fileUrl[0].url) {
        throw new Error('Invalid file URL received');
      }

      worker.postMessage({
        file: { ...nextFileObj.file, url: fileUrl[0].url },
      });

      worker.onmessage = async (event) => {
        const { success, file, blob, error } = event.data;
        if (success) {
          await zipStream.addFile(file.file_name, blob);
        } else {
          zipStream.errorLog.push(`${file.file_name} - ERROR: ${error}`);
          useStore.getState().addError(`${file.file_name} - ERROR: ${error}`);
        }

        activeWorkers--;

        if (fileQueue.length > 0) {
          await assignFileToWorker(worker);
        }

        if (fileQueue.length === 0 && activeWorkers === 0) {
          setTimeout(async () => {
            if (zipStream.endTime === 0) {
              await zipStream.finish();
              workers.forEach((worker) => worker.terminate());
            }
          }, 500);
        }
      };
    } catch (error) {
      zipStream.errorLog.push(
        `${nextFileObj.fileName} - ERROR: ${error.message}`
      );
      useStore
        .getState()
        .addError(`${nextFileObj.fileName} - ERROR: ${error.message}`);
      activeWorkers--;
      return await assignFileToWorker(worker);
    }
  };

  for (const worker of workers) {
    await assignFileToWorker(worker);
  }
};

// export const downloadMultipleVersionFiles = async ({
//   files,
//   tenderId,
//   currentFolderId,
//   rootFolderId,
//   onDownloadStarted,
// }: DownloadMultipleAttachmentsParams) => {
//   const fileData: {
//     blob: Blob;
//     location: string;
//     name: string;
//     path?: string;
//   }[] = [];
//
//   useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);
//
//   await Promise.all(
//     files.map(async (file) => {
//       const fileLocation = await getTenderVersionedFileLocation({
//         attachmentId: String(file.files?.[0].id),
//         versionedFileId: String(file.id),
//         projectId: String(file.project_id),
//         tenderId,
//         currentFolderId,
//         rootFolderId,
//       });
//
//       if (fileLocation) {
//         const response = await fetch(fileLocation);
//         const blob = await response.blob();
//         fileData.push({
//           blob: blob,
//           location: fileLocation,
//           name: file.title,
//         });
//       }
//     })
//   );
//
//   if (!fileData.length) return;
//
//   const totalSize = fileData.reduce((total, current) => {
//     total += current.blob.size;
//     return total;
//   }, 0);
//
//   onDownloadStarted && onDownloadStarted(totalSize);
//
//   useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
//   setTimeout(() => {
//     useStore.getState().changeDownloadingActive(false);
//   }, 3_000);
//
//   return saveFilesToZip(fileData);
// };
